import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';

// Track band filter definitions — maps each DAW track to a frequency range.
// NOTE: this is master frequency shaping, not true per-stem mixing.
// Using shelf filters for extremes and wide-Q peaking for mids
// Lower Q = wider bandwidth = more audible mute/solo effect
const TRACK_BANDS = {
  drums:  { type: 'lowshelf',  freq: 150,  Q: 0.7 },
  bass:   { type: 'peaking',   freq: 200,  Q: 0.4 },
  melody: { type: 'peaking',   freq: 1500, Q: 0.4 },
  chords: { type: 'peaking',   freq: 600,  Q: 0.5 },
  voz:    { type: 'peaking',   freq: 3000, Q: 0.5 },
  fx:     { type: 'highshelf', freq: 6000, Q: 0.7 },
};
const TRACK_ORDER = ['drums', 'bass', 'melody', 'chords', 'voz', 'fx'];

// FFT bin ranges for per-track VU metering (fftSize=256, ~172Hz/bin at 44.1kHz)
const TRACK_FFT_RANGES = {
  drums:  [[0, 2], [46, 64]],
  bass:   [[0, 4]],
  melody: [[5, 18]],
  chords: [[2, 7]],
  voz:    [[12, 40]],
  fx:     [[40, 80]],
};

const WaveSurferPlayer = forwardRef(function WaveSurferPlayer({
  audioUrl,
  autoPlay = true,
  loop = false,
  loopRegion = null,
  waveColor = '#F5C84230',
  progressColor = '#F5C842',
  cursorColor = '#ffffffCC',
  height = 48,
  barWidth = 2,
  barGap = 1,
  barRadius = 2,
  onReady,
  onPlay,
  onPause,
  onFinish,
  onTimeUpdate,
}, ref) {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Audio processing refs
  const audioContextRef = useRef(null);
  const eqNodesRef = useRef(null);
  const fxNodesRef = useRef(null);
  const trackFiltersRef = useRef(null);
  const analyserRef = useRef(null);
  const masterOutputRef = useRef(null);
  const trackGainStateRef = useRef({});

  const cbRef = useRef({ onReady, onPlay, onPause, onFinish, onTimeUpdate, autoPlay, loop, loopRegion });
  useEffect(() => {
    cbRef.current = { onReady, onPlay, onPause, onFinish, onTimeUpdate, autoPlay, loop, loopRegion };
  });

  // Build complete audio processing chain:
  // source → trackBands → EQ → FX → masterOutput → analyser → speakers
  const initAudioProcessing = useCallback(() => {
    if (!wsRef.current || audioContextRef.current) return;

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
      audioContextRef.current = ctx;

      const mediaEl = wsRef.current.getMediaElement?.();
      if (!mediaEl) return;

      const source = ctx.createMediaElementSource(mediaEl);

      // ── Track band filters (M/S/Volume) ──
      const trackFilters = {};
      TRACK_ORDER.forEach(trackId => {
        const spec = TRACK_BANDS[trackId];
        const filter = ctx.createBiquadFilter();
        filter.type = spec.type;
        filter.frequency.value = spec.freq;
        if (spec.type === 'peaking') filter.Q.value = spec.Q;
        filter.gain.value = 0; // Neutral = transparent
        trackFilters[trackId] = filter;
      });
      trackFiltersRef.current = trackFilters;

      // Chain: source → drums → bass → melody → chords → voz → fx
      source.connect(trackFilters.drums);
      trackFilters.drums.connect(trackFilters.bass);
      trackFilters.bass.connect(trackFilters.melody);
      trackFilters.melody.connect(trackFilters.chords);
      trackFilters.chords.connect(trackFilters.voz);
      trackFilters.voz.connect(trackFilters.fx);

      // ── EQ: Bass / Mid / Treble ──
      const bassEQ = ctx.createBiquadFilter();
      bassEQ.type = 'lowshelf'; bassEQ.frequency.value = 200; bassEQ.gain.value = 0;
      const midEQ = ctx.createBiquadFilter();
      midEQ.type = 'peaking'; midEQ.frequency.value = 1000; midEQ.Q.value = 1; midEQ.gain.value = 0;
      const trebleEQ = ctx.createBiquadFilter();
      trebleEQ.type = 'highshelf'; trebleEQ.frequency.value = 4000; trebleEQ.gain.value = 0;
      eqNodesRef.current = { bass: bassEQ, mid: midEQ, treble: trebleEQ };

      trackFilters.fx.connect(bassEQ);
      bassEQ.connect(midEQ);
      midEQ.connect(trebleEQ);

      // ── FX: Filter / Delay / Reverb ──
      const filterFX = ctx.createBiquadFilter();
      filterFX.type = 'lowpass'; filterFX.frequency.value = 20000; filterFX.Q.value = 0.7;
      trebleEQ.connect(filterFX);

      const delayFX = ctx.createDelay(1.0);
      delayFX.delayTime.value = 0;
      const delayFeedback = ctx.createGain(); delayFeedback.gain.value = 0;
      const delayWet = ctx.createGain(); delayWet.gain.value = 0;
      const delayDry = ctx.createGain(); delayDry.gain.value = 1.0;

      const reverbFX = ctx.createConvolver();
      const reverbWet = ctx.createGain(); reverbWet.gain.value = 0;
      const reverbDry = ctx.createGain(); reverbDry.gain.value = 1.0;

      const sampleRate = ctx.sampleRate;
      const length = sampleRate * 1.5;
      const impulse = ctx.createBuffer(2, length, sampleRate);
      for (let ch = 0; ch < 2; ch++) {
        const data = impulse.getChannelData(ch);
        for (let i = 0; i < length; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5);
        }
      }
      reverbFX.buffer = impulse;

      fxNodesRef.current = { filter: filterFX, delay: delayFX, delayFeedback, delayWet, delayDry, reverb: reverbFX, reverbWet, reverbDry };

      // FX routing
      filterFX.connect(delayDry);
      filterFX.connect(delayFX);
      delayFX.connect(delayFeedback);
      delayFeedback.connect(delayFX);
      delayFX.connect(delayWet);
      filterFX.connect(reverbDry);
      filterFX.connect(reverbFX);
      reverbFX.connect(reverbWet);

      // ── Master output + Analyser ──
      const masterOutput = ctx.createGain();
      masterOutput.gain.value = 1.0;
      masterOutputRef.current = masterOutput;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.7;
      analyserRef.current = analyser;

      delayDry.connect(masterOutput);
      delayWet.connect(masterOutput);
      reverbDry.connect(masterOutput);
      reverbWet.connect(masterOutput);

      masterOutput.connect(analyser);
      analyser.connect(ctx.destination);

      // Re-apply stored track gains (survives audio reload)
      const stored = trackGainStateRef.current;
      if (stored) {
        Object.entries(stored).forEach(([id, gain]) => {
          if (trackFilters[id]) {
            trackFilters[id].gain.setValueAtTime(gain, ctx.currentTime);
          }
        });
      }
    } catch (err) {
      console.warn('Audio processing init failed:', err);
    }
  }, []);

  // Create WaveSurfer instance
  useEffect(() => {
    if (!containerRef.current) return;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor,
      progressColor,
      cursorColor,
      cursorWidth: 2,
      height,
      barWidth,
      barGap,
      barRadius,
      normalize: true,
      fillParent: true,
      hideScrollbar: true,
      backend: 'MediaElement',
    });

    ws.on('ready', () => {
      setLoading(false);
      setTimeout(() => initAudioProcessing(), 100);
      if (cbRef.current.autoPlay) ws.play().catch(() => {});
      cbRef.current.onReady?.({
        duration: ws.getDuration(),
        play: () => ws.play(),
        pause: () => ws.pause(),
        playPause: () => ws.playPause(),
        seekTo: (p) => ws.seekTo(Math.max(0, Math.min(1, p))),
        getDuration: () => ws.getDuration(),
        getCurrentTime: () => ws.getCurrentTime(),
        isPlaying: () => ws.isPlaying(),
      });
    });

    ws.on('play', () => {
      audioContextRef.current?.resume();
      cbRef.current.onPlay?.();
    });
    ws.on('pause', () => cbRef.current.onPause?.());

    ws.on('finish', () => {
      if (cbRef.current.loop) {
        ws.seekTo(0);
        ws.play().catch(() => {});
      } else {
        cbRef.current.onFinish?.();
      }
    });

    ws.on('timeupdate', (currentTime) => {
      const dur = ws.getDuration();
      const lr = cbRef.current.loopRegion;
      if (lr && dur > 0) {
        const loopStart = (lr.startPct ?? lr.start ?? null);
        const loopEnd = (lr.endPct ?? lr.end ?? null);
        if (loopStart != null && loopEnd != null) {
          const endTime = loopEnd * dur;
          const startTime = loopStart * dur;
          if (currentTime >= endTime && endTime > startTime) {
            ws.seekTo(Math.max(0, loopStart));
            return;
          }
        }
      }
      cbRef.current.onTimeUpdate?.({
        currentTime,
        duration: dur,
        progress: dur > 0 ? currentTime / dur : 0,
      });
    });

    ws.on('error', (err) => {
      setLoading(false);
      console.warn('WaveSurfer error:', err);
    });

    wsRef.current = ws;
    return () => {
      ws.destroy();
      wsRef.current = null;
      audioContextRef.current?.close();
      audioContextRef.current = null;
    };
  }, [initAudioProcessing]);

  // Load audio on URL change
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || !audioUrl) return;
    setLoading(true);
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      eqNodesRef.current = null;
      fxNodesRef.current = null;
      trackFiltersRef.current = null;
      analyserRef.current = null;
      masterOutputRef.current = null;
    }
    ws.load(audioUrl);
  }, [audioUrl]);

  // Expose player API
  useImperativeHandle(ref, () => ({
    play: () => wsRef.current?.play(),
    pause: () => wsRef.current?.pause(),
    playPause: () => wsRef.current?.playPause(),
    stop: () => { wsRef.current?.pause(); wsRef.current?.seekTo(0); },
    seekTo: (p) => wsRef.current?.seekTo(Math.max(0, Math.min(1, p))),
    getCurrentTime: () => wsRef.current?.getCurrentTime() || 0,
    getDuration: () => wsRef.current?.getDuration() || 0,
    isPlaying: () => wsRef.current?.isPlaying() || false,

    // EQ control (0-10 scale, 5=neutral)
    setEQ: (band, value) => {
      if (!eqNodesRef.current?.[band]) return;
      const gainDb = (value - 5) * 2.4;
      eqNodesRef.current[band].gain.setValueAtTime(gainDb, audioContextRef.current?.currentTime || 0);
    },

    // Filter cutoff (0-20 kHz scale)
    setFilter: (freqKHz) => {
      if (!fxNodesRef.current?.filter) return;
      const freq = Math.max(100, Math.min(20000, freqKHz * 1000));
      fxNodesRef.current.filter.frequency.setValueAtTime(freq, audioContextRef.current?.currentTime || 0);
    },

    // Delay amount (0-10 scale)
    setDelay: (amount) => {
      if (!fxNodesRef.current?.delay) return;
      const t = audioContextRef.current?.currentTime || 0;
      fxNodesRef.current.delay.delayTime.setValueAtTime((amount / 10) * 0.4, t);
      fxNodesRef.current.delayWet.gain.setValueAtTime((amount / 10) * 0.5, t);
      fxNodesRef.current.delayFeedback.gain.setValueAtTime((amount / 10) * 0.35, t);
    },

    // Reverb amount (0-10 scale)
    setReverb: (amount) => {
      if (!fxNodesRef.current?.reverbWet) return;
      const t = audioContextRef.current?.currentTime || 0;
      const wet = (amount / 10) * 0.5;
      fxNodesRef.current.reverbWet.gain.setValueAtTime(wet, t);
      fxNodesRef.current.reverbDry.gain.setValueAtTime(1 - wet * 0.3, t);
    },

    hasProcessing: () => !!audioContextRef.current,

    // ── Track band control (M/S/Volume) ──
    setTrackGain: (trackId, gainDb) => {
      const clamped = Math.max(-30, Math.min(6, gainDb));
      if (trackFiltersRef.current?.[trackId]) {
        trackFiltersRef.current[trackId].gain.setValueAtTime(clamped, audioContextRef.current?.currentTime || 0);
      }
      trackGainStateRef.current[trackId] = clamped;
    },

    // Get per-track frequency levels (0-1) from real analyser
    getBandLevels: () => {
      if (!analyserRef.current) return null;
      const buf = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(buf);
      const levels = {};
      for (const [trackId, ranges] of Object.entries(TRACK_FFT_RANGES)) {
        let sum = 0, count = 0;
        for (const [lo, hi] of ranges) {
          for (let i = lo; i <= Math.min(hi, buf.length - 1); i++) {
            sum += buf[i]; count++;
          }
        }
        levels[trackId] = count > 0 ? (sum / count) / 255 : 0;
      }
      return levels;
    },

    seekToLoopStart: () => {
      const lr = cbRef.current.loopRegion;
      if (lr && lr.start != null) wsRef.current?.seekTo(Math.max(0, lr.start));
    },
  }));

  return (
    <div className="relative w-full" data-testid="wavesurfer-player">
      <div ref={containerRef} style={{ minHeight: height }} />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#080809]/60 rounded">
          <div className="flex items-center gap-2 text-[10px] text-[#F5C842]">
            <div className="w-3 h-3 border-2 border-[#F5C842] border-t-transparent rounded-full animate-spin" />
            Cargando audio...
          </div>
        </div>
      )}
    </div>
  );
});

WaveSurferPlayer.displayName = 'WaveSurferPlayer';
export default WaveSurferPlayer;
